import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteFooter from '../components/SiteFooter';
import UserGuidesSection from '../components/UserGuidesSection';
import FaqByRoleSection from '../components/FaqByRoleSection';
import { fetchPublicHelp } from '../utils/publicHelpApi';
import './HelpSupport.css';

const HelpSupport = () => {
  const [activeTab, setActiveTab] = useState('guides');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [faqAudience, setFaqAudience] = useState('admin');
  const [searchInput, setSearchInput] = useState('');

  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [guidesLoading, setGuidesLoading] = useState(true);
  const [faqLoading, setFaqLoading] = useState(false);
  const [error, setError] = useState(null);

  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const loadGuides = useCallback(async (categorySlug, signal) => {
    setGuidesLoading(true);
    setError(null);
    try {
      const data = await fetchPublicHelp({ category: categorySlug || undefined }, signal);
      setCategories(Array.isArray(data?.categories) ? data.categories : []);
      setArticles(Array.isArray(data?.articles) ? data.articles : []);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setCategories([]);
      setArticles([]);
      setError(err instanceof Error ? err.message : 'Failed to load help content');
    } finally {
      if (!signal?.aborted) setGuidesLoading(false);
    }
  }, []);

  const loadFaqs = useCallback(async (audience, signal) => {
    setFaqLoading(true);
    setError(null);
    try {
      const data = await fetchPublicHelp({ audience }, signal);
      setFaqs(Array.isArray(data?.faqs) ? data.faqs : []);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setFaqs([]);
      setError(err instanceof Error ? err.message : 'Failed to load FAQs');
    } finally {
      if (!signal?.aborted) setFaqLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'user-guides') {
      setActiveTab('user-guides');
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'guides') return undefined;
    const controller = new AbortController();
    loadGuides(selectedCategory, controller.signal);
    return () => controller.abort();
  }, [activeTab, loadGuides, selectedCategory]);

  useEffect(() => {
    if (activeTab !== 'faq') return undefined;
    const controller = new AbortController();
    loadFaqs(faqAudience, controller.signal);
    return () => controller.abort();
  }, [activeTab, loadFaqs, faqAudience]);

  const searchTerm = searchInput.trim().toLowerCase();

  const filteredArticles = useMemo(() => {
    if (!searchTerm) return articles;
    return articles.filter((article) => {
      const haystack = [article.title, article.description, article.category_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [articles, searchTerm]);

  const openArticle = (articleId) => {
    const article = articles.find((item) => item.id === articleId);
    if (!article) return;
    setSelectedArticle(article);
    setArticleModalOpen(true);
  };

  const closeArticleModal = () => {
    setArticleModalOpen(false);
    setSelectedArticle(null);
  };

  const categoryBadgeCount = (category) => category.article_count ?? 0;

  const showApiSections = activeTab === 'guides' || activeTab === 'faq';
  const showSearch =
    activeTab === 'guides' || activeTab === 'faq' || activeTab === 'user-guides';

  return (
    <div className="d-flex flex-column min-vh-100 help-support-page">
      <main className="flex-grow-1" style={{ paddingTop: '88px' }}>
        <div className="container py-4 py-lg-5">
          <Link to="/" className="help-back-link">
            &larr; Back to Home
          </Link>

          <div className="help-page-hero text-center">
            <h1>Help &amp; Support</h1>
            <p className="mb-0 opacity-90">
              Browse guides and FAQs from our help center, or follow step-by-step user guides for each role.
            </p>
            {showSearch && (
              <div className="help-page-search">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search guides, tutorials, FAQs..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  aria-label="Search help content"
                />
              </div>
            )}
          </div>

          <ul className="nav nav-pills help-page-tabs justify-content-center gap-2 mb-4 flex-wrap">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'guides' ? 'active' : ''}`}
                onClick={() => setActiveTab('guides')}
              >
                Guides
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'faq' ? 'active' : ''}`}
                onClick={() => setActiveTab('faq')}
              >
                FAQ
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'user-guides' ? 'active' : ''}`}
                onClick={() => setActiveTab('user-guides')}
              >
                User Guides
              </button>
            </li>
          </ul>

          {showApiSections && error && (
            <div className="help-error-banner mb-4" role="alert">
              {error}
            </div>
          )}

          {activeTab === 'guides' && (
            <div className="row g-4">
              <div className="col-lg-3">
                <div className="card help-category-card">
                  <div className="card-header bg-white fw-bold">Categories</div>
                  <div className="list-group list-group-flush">
                    <button
                      type="button"
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                        !selectedCategory ? 'active' : ''
                      }`}
                      onClick={() => setSelectedCategory('')}
                    >
                      <span>All topics</span>
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.slug}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                          selectedCategory === category.slug ? 'active' : ''
                        }`}
                        onClick={() => setSelectedCategory(category.slug)}
                      >
                        <span>{category.name}</span>
                        <span className="badge rounded-pill bg-light text-dark">
                          {categoryBadgeCount(category)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-lg-9">
                {guidesLoading ? (
                  <div className="row g-3">
                    {[1, 2, 3, 4].map((n) => (
                      <div className="col-md-6" key={n}>
                        <div className="help-skeleton" />
                      </div>
                    ))}
                  </div>
                ) : filteredArticles.length === 0 ? (
                  <div className="card help-faq-card">
                    <div className="help-empty">No guides found. Try another category or search term.</div>
                  </div>
                ) : (
                  <div className="row g-3">
                    {filteredArticles.map((article) => (
                      <div className="col-md-6" key={article.id}>
                        <div
                          className="card help-article-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => openArticle(article.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openArticle(article.id);
                            }
                          }}
                        >
                          <div className="card-body">
                            {article.category_name && (
                              <span className="badge mb-2">{article.category_name}</span>
                            )}
                            <h5 className="card-title fw-bold">{article.title}</h5>
                            {article.description && (
                              <p className="card-text text-muted small mb-0">{article.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'faq' && (
            <FaqByRoleSection
              faqs={faqs}
              loading={faqLoading}
              initialAudience={faqAudience}
              searchTerm={searchInput}
              onAudienceChange={setFaqAudience}
            />
          )}

          {activeTab === 'user-guides' && <UserGuidesSection searchTerm={searchInput} />}

          {/* Screen inventory removed — designer brief: public/user-guides/DESIGNER_USER_GUIDE_CALLOUTS.txt */}

          <p className="text-center text-muted small mt-4 mb-0">
            Need more help?{' '}
            <Link to="/contact" className="text-primary fw-semibold">
              Contact us
            </Link>
            .
          </p>
        </div>
      </main>

      {articleModalOpen && selectedArticle && (
        <div
          className="modal fade show help-article-modal d-block"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)' }}
          role="dialog"
          aria-modal="true"
          onClick={closeArticleModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">{selectedArticle.title}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeArticleModal} />
              </div>
              <div className="modal-body">
                <div
                  className="help-article-content"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content || '' }}
                />
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-secondary rounded-pill" onClick={closeArticleModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
};

export default HelpSupport;
